import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export interface User {
    id: string
    email: string
    firstName: string
    lastName: string
    roles: string[]
    permissions: string[]
}

export interface AuthState {
    user: User | null
    accessToken: string | null
    refreshToken: string | null
    isAuthenticated: boolean
    isLoading: boolean
}

export interface AuthActions {
    login: (user: User, accessToken: string, refreshToken: string) => void
    logout: () => void
    updateUser: (user: Partial<User>) => void
    setLoading: (loading: boolean) => void
    refreshAccessToken: (accessToken: string) => void
    setRefreshToken: (refreshToken: string) => void
}

export type AuthStore = AuthState & AuthActions

export const useAuthStore = create<AuthStore>()(
    persist(
        (set, get) => ({
            // State
            user: null,
            accessToken: null,
            refreshToken: null,
            isAuthenticated: false,
            isLoading: false,

            // Actions
            login: (user: User, accessToken: string, refreshToken: string) => {
                set({
                    user,
                    accessToken,
                    refreshToken,
                    isAuthenticated: true,
                    isLoading: false
                })
            },

            logout: () => {
                set({
                    user: null,
                    accessToken: null,
                    refreshToken: null,
                    isAuthenticated: false,
                    isLoading: false
                })
            },

            updateUser: (userData: Partial<User>) => {
                const currentUser = get().user
                if (currentUser) {
                    set({
                        user: { ...currentUser, ...userData }
                    })
                }
            },

            setLoading: (loading: boolean) => {
                set({ isLoading: loading })
            },

            refreshAccessToken: (accessToken: string) => {
                set({ accessToken })
            },

            setRefreshToken: (refreshToken: string) => {
                set({ refreshToken })
            }
        }),
        {
            name: 'auth-storage',
            partialize: (state) => ({
                user: state.user,
                accessToken: state.accessToken,
                refreshToken: state.refreshToken,
                isAuthenticated: state.isAuthenticated
            })
        }
    )
)
